/**
 * Korean locale for webhooks-ui.
 * Export is the "webhooks" subtree: host apps merge this into their i18n
 * resources as translation.webhooks so that t('webhooks.list.integrated') etc. resolve.
 */
import type { WebhooksLocale } from './en'

export const webhooksKo = {
  common: {
    createIntegration: `연동 생성`,
    updateIntegration: `연동 업데이트`,
    failedToSave: `저장 실패`,
    learnMore: `자세히 보기`,
    beta: `베타`,
  },
  list: {
    failedToLoad: `연동 목록을 불러오지 못했습니다`,
    noIntegrationsAvailable: `사용 가능한 연동이 없습니다`,
    integrated: `연동됨`,
    learnMore: `자세히 보기`,
  },
  detail: {
    status: `상태`,
    on: `켜짐`,
    off: `꺼짐`,
    failedToLoad: `연동을 불러오지 못했습니다`,
    back: `← 뒤로`,
    webhooksV2Title: `Webhooks v2.0`,
    xlDentTitle: `스크린 팝 연동`,
    integrationTitle: `{{name}} 연동`,
    webhooksV2Description: `이벤트 페이로드는 제공하신 콜백 URL로 전송됩니다. 이벤트 선택기를 사용하여 전송할 이벤트를 지정해야 합니다`,
    xlDentDescription: `누가 전화하는지 알아보세요. 시간을 절약하세요. 빠르게 답변을 확인하세요. 수신 및 발신 통화에서 스크린 팝 알림을 받으세요`,
    remoteIdLabel: `원격 ID`,
    practiceGuidLabel: `진료소 GUID`,
  },
  dolphin: {
    webhookUrl: `웹훅 URL`,
  },
  webhooksV2: {
    remoteId: `원격 ID`,
    remoteIdPlaceholder: `소스 PBX를 식별하는 데 사용됩니다.`,
    callbackUrl: `콜백 URL`,
    callbackUrlPlaceholder: `https://yourdomain.com/mango/event-handler`,
    eventSelectors: `이벤트 선택기`,
    eventSelectorsLoadError: `이벤트 선택기를 불러올 수 없습니다 — 새로고침하여 다시 시도해 주세요.`,
    noEventsWarning: `이벤트 선택기를 선택하지 않았습니다. 해당 URL로 이벤트가 전송되지 않습니다. 그래도 저장하시겠습니까?`,
    goBack: `뒤로 가기`,
    saveAnyway: `그래도 저장`,
  },
  xlDent: {
    tableDescription: `Mango Voice 내선을 워크스테이션 IP 주소에 매핑합니다.`,
    extensionName: `내선 이름`,
    extensionNumber: `내선 번호`,
    workstationIp: `워크스테이션 IP 주소`,
    enableDisable: `활성화/비활성화`,
    disabled: `비활성화됨.`,
    ipPlaceholder: `e.g. 192.168.1.1:8888`,
    update: `업데이트`,
    devicesNote: `참고: 변경 사항을 적용하려면 '내 기기'에서 기기를 업데이트해야 합니다.`,
  },
  // 이 섹션의 텍스트는 apidocs.mangovoice.com과 일관된 언어를 사용하기 위해
  // 의도적으로 API 문서와 동일한 표현으로 작성되었습니다.
  authKeys: {
    title: `인증 키`,
    description: `Mango Voice 글로벌 API에 대한 요청을 인증하는 3부분 토큰을 생성하세요. 통합 파트너와 공유하면 파트너가 귀하를 대신하여 API를 호출할 수 있습니다.`,
    confidential: `토큰은 반드시 기밀로 유지해야 합니다. 토큰을 공개적으로 노출하면 데이터 도난, 손실 또는 손상이 발생할 수 있습니다.`,
    noKeys: `아직 인증 키가 생성되지 않았습니다.`,
    generate: `생성`,
    regenerate: `재생성`,
    remove: `삭제`,
    copy: `복사`,
    copied: `복사됨!`,
    apiDocsLabel: `API 문서 보기`,
    failedToLoad: `인증 키를 불러오지 못했습니다`,
    failedToGenerate: `인증 키를 생성하지 못했습니다`,
    failedToRegenerate: `인증 키를 재생성하지 못했습니다`,
    failedToRemove: `인증 키를 삭제하지 못했습니다`,
    confirmRegenerateTitle: `인증 키 재생성`,
    confirmRegenerateBody: `인증 키를 재생성하면 기존 통합이 비활성화될 수 있습니다. 계속하시겠습니까?`,
    confirmRemoveTitle: `인증 키 삭제`,
    confirmRemoveBody: `인증 키를 삭제하면 기존 통합이 비활성화될 수 있습니다. 계속하시겠습니까?`,
    confirmCancel: `취소`,
    confirmProceed: `진행`,
    combinedToken: `통합 토큰`,
    combinedTokenHint: `API 요청의 경로 변수로 사용됨`,
    formatIdent1: `ident1`,
    formatIdent2: `ident2`,
    formatIdent3: `ident3`,
  },
  admin: {
    apiAccess: {
      title: `API 접근`,
      selectPbx: `PBX 선택`,
      selectCompanyFirst: `먼저 회사를 선택하세요`,
      noPbxSelected: `인증 키를 관리하려면 위에서 회사와 PBX를 선택하세요.`,
    },
    // admin.list is an internal admin3 namespace — English strings are intentional
    list: {
      title: `Webhook Integrations`,
      description: `View and manage webhook integration status across all PBXes for a company`,
      companyLabel: `Company`,
      companyPlaceholder: `Select a company…`,
      pbxFilter: `PBX`,
      allPbxes: `All PBXes`,
      integrationFilter: `Integration`,
      allIntegrations: `All Integrations`,
      noCompanySelected: `Select a company to view integrations`,
      noIntegrationsFound: `No integrations found`,
      columnStatus: `Status`,
      columnActive: `Active`,
      notIntegrated: `Not integrated`,
    },
  },
} satisfies WebhooksLocale
